import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Box,
    FormControlLabel,
    Switch,
} from '@mui/material';
// (Removed unused date-fns imports)

interface Auxiliaire {
    id: number;
    name: string;
    category: string;
}

interface Schedule {
    id: number;
    auxiliaire_id: number;
    start_date: string;
    end_date: string;
    start_time: string;
    end_time: string;
    is_recurring: boolean;
    recurrence_group_id?: string;
}

interface ScheduleEditDialogProps {
    open: boolean;
    onClose: () => void;
    schedule: Schedule | null;
    auxiliaires: Auxiliaire[];
    onSave: (schedule: Omit<Schedule, 'id' | 'auxiliaire'>) => Promise<void>;
}

const ScheduleEditDialog: React.FC<ScheduleEditDialogProps> = ({
    open,
    onClose,
    schedule,
    auxiliaires,
    onSave,
}) => {
    const [formData, setFormData] = useState<Omit<Schedule, 'id' | 'auxiliaire'>>({
        auxiliaire_id: 0,
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: '',
        is_recurring: false,
        recurrence_group_id: undefined,
    });

    useEffect(() => {
        if (schedule) {
            setFormData({
                auxiliaire_id: schedule.auxiliaire_id,
                start_date: schedule.start_date,
                end_date: schedule.end_date,
                start_time: schedule.start_time,
                end_time: schedule.end_time,
                is_recurring: schedule.is_recurring,
                recurrence_group_id: schedule.recurrence_group_id,
            });
        }
    }, [schedule]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await onSave(formData);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Modifier le planning</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            select
                            label="Auxiliaire"
                            name="auxiliaire_id"
                            value={formData.auxiliaire_id}
                            onChange={handleChange}
                            required
                            fullWidth
                        >
                            {auxiliaires.map(aux => (
                                <MenuItem key={aux.id} value={aux.id}>
                                    {aux.name}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label="Date de début"
                            name="start_date"
                            type="date"
                            value={formData.start_date}
                            onChange={handleChange}
                            required
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />

                        <TextField
                            label="Date de fin"
                            name="end_date"
                            type="date"
                            value={formData.end_date}
                            onChange={handleChange}
                            required
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />

                        <TextField
                            label="Heure de début"
                            name="start_time"
                            type="time"
                            value={formData.start_time}
                            onChange={handleChange}
                            required
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                step: 300, // 5 minutes
                            }}
                        />

                        <TextField
                            label="Heure de fin"
                            name="end_time"
                            type="time"
                            value={formData.end_time}
                            onChange={handleChange}
                            required
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                step: 300, // 5 minutes
                            }}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_recurring}
                                    onChange={handleChange}
                                    name="is_recurring"
                                />
                            }
                            label="Planning récurrent"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Annuler</Button>
                    <Button type="submit" variant="contained" color="primary">
                        Enregistrer
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ScheduleEditDialog; 